import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { ChevronLeft, Download } from "lucide-react";

const API = "https://api.komorotel.com/api";

const euros = (n) => `${(Math.round((n || 0) * 100) / 100).toLocaleString("fr-FR")} €`;
const jour  = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const moisLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};

export default function OrgStatements() {
  const [mois, setMois]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [ouvert, setOuvert]   = useState(null);

  useEffect(() => {
    axios.get(`${API}/organization/statements`, { withCredentials: true })
      .then(r => setMois(r.data))
      .catch(() => toast.error("Impossible de charger les relevés."))
      .finally(() => setLoading(false));
  }, []);

  if (ouvert) return <Detail month={ouvert} onBack={() => setOuvert(null)} />;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[980px]">

      <header className="mb-7">
        <h1 className="text-[23px] sm:text-[27px] leading-tight font-semibold tracking-[-0.01em] text-[var(--org-ink)]">
          Relevés
        </h1>
        <p className="text-[13.5px] sm:text-[13.5px] text-[var(--org-slate)] mt-1 max-w-xl">
          Un relevé par mois, qui regroupe les séjours selon leur date d'arrivée
        </p>
      </header>

      <div style={mois.length > 0 || loading ? { borderTop: "1px solid var(--org-line)" } : undefined}>
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="py-6" style={{ borderBottom: "1px solid var(--org-line)" }}>
              <div className="h-4 w-40 bg-gray-200/60 rounded animate-pulse" />
            </div>
          ))
        ) : mois.length === 0 ? (
          <div className="max-w-xl">
            <p className="text-[14px] text-[var(--org-ink)]">Aucun séjour à facturer pour l'instant.</p>
            <p className="text-[13.5px] text-[var(--org-slate)] mt-1.5 leading-relaxed">
              Dès qu'un membre part, son séjour rejoint le relevé du mois de son
              arrivée. Vous y trouverez le détail par personne et le total à régler.
            </p>
          </div>
        ) : (
          mois.map(m => (
            <button
              key={m.month}
              onClick={() => setOuvert(m.month)}
              className="w-full text-left py-5 flex items-baseline justify-between gap-4 transition hover:bg-white"
              style={{ borderBottom: "1px solid var(--org-line)" }}
            >
              <div>
                <p className="text-[17px] sm:text-[16px] text-[var(--org-ink)] capitalize">
                  {moisLabel(m.month)}
                </p>
                <p className="text-[13px] text-[var(--org-slate)] mt-0.5">
                  <span className="org-num">{m.reservations}</span> séjour{m.reservations > 1 ? "s" : ""},{" "}
                  <span className="org-num">{m.nights}</span> nuit{m.nights > 1 ? "s" : ""},{" "}
                  <span className="org-num">{m.travellers}</span> voyageur{m.travellers > 1 ? "s" : ""}
                </p>
              </div>
              <p className="org-num text-[17px] font-medium text-[var(--org-ink)] shrink-0">
                {euros(m.total)}
              </p>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function Detail({ month, onBack }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/organization/statements/${month}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => toast.error("Impossible de charger ce relevé."))
      .finally(() => setLoading(false));
  }, [month]);

  /* Export monté côté navigateur : le serveur renvoie déjà les lignes,
     une route de plus pour reformater les mêmes données n'apporterait rien. */
  const exporter = () => {
    if (!data) return;
    const entete = ["Reference", "Voyageur", "Mission", "Hebergement", "Ville", "Arrivee", "Depart", "Nuits", "Taxe", "Total"];
    const lignes = data.lines.map(l => [
      l.reservationId, l.traveller, l.mission || "", l.hotel, l.city,
      new Date(l.checkInDate).toLocaleDateString("fr-FR"),
      new Date(l.checkOutDate).toLocaleDateString("fr-FR"),
      l.nights,
      String(l.taxAmount ?? 0).replace(".", ","),
      String(l.total ?? 0).replace(".", ","),
    ]);
    // Point-virgule et BOM : sans eux, Excel en français colle tout dans une
    // colonne et abîme les accents.
    const csv = [entete, ...lignes]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\r\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `releve-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[980px]">

      <button
        onClick={onBack}
        className="flex items-center gap-1 text-[13.5px] text-[var(--org-slate)] hover:text-[var(--org-ink)] transition mb-5"
      >
        <ChevronLeft size={16} strokeWidth={1.75} /> Tous les relevés
      </button>

      <header className="flex items-start justify-between gap-4 mb-7">
        <div>
          <h1 className="text-[23px] sm:text-[27px] leading-tight font-semibold tracking-[-0.01em] text-[var(--org-ink)] capitalize">
            {moisLabel(month)}
          </h1>
          {data && (
            <p className="text-[13.5px] sm:text-[13.5px] text-[var(--org-slate)] mt-1">
              {data.organization.name}
              {data.organization.paymentTermsDays
                ? `, règlement à ${data.organization.paymentTermsDays} jours`
                : ""}
            </p>
          )}
        </div>
        {data && data.lines.length > 0 && (
          <button
            onClick={exporter}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13.5px] text-[var(--org-slate)] hover:text-[var(--org-ink)] transition shrink-0"
            style={{ border: "1px solid var(--org-line)" }}
          >
            <Download size={15} strokeWidth={1.75} /> CSV
          </button>
        )}
      </header>

      {loading ? (
        <div className="h-64 rounded-lg bg-gray-200/40 animate-pulse" />
      ) : !data || data.lines.length === 0 ? (
        <div className="py-14" style={{ borderTop: "1px solid var(--org-line)", borderBottom: "1px solid var(--org-line)" }}>
          <p className="text-[15px] text-[var(--org-slate)]">Aucun séjour sur ce mois.</p>
        </div>
      ) : (
        <>
          <div style={{ borderTop: "1px solid var(--org-line)" }}>
            {data.lines.map(l => (
              <div
                key={l.reservationId}
                className="py-4 sm:flex sm:items-baseline sm:gap-6"
                style={{ borderBottom: "1px solid var(--org-line)" }}
              >
                <div className="sm:w-[150px] shrink-0">
                  <p className="org-num text-[14px] text-[var(--org-ink)]">
                    {jour(l.checkInDate)} au {jour(l.checkOutDate)}
                  </p>
                  <p className="text-[12.5px] text-[var(--org-slate)] mt-0.5">
                    {l.nights} nuit{l.nights > 1 ? "s" : ""}
                  </p>
                </div>

                <div className="flex-1 min-w-0 mt-2 sm:mt-0">
                  <p className="text-[14px] text-[var(--org-ink)] truncate">{l.traveller}</p>
                  <p className="text-[13px] text-[var(--org-slate)] truncate mt-0.5">
                    {l.hotel}{l.city ? `, ${l.city}` : ""}
                  </p>
                  {l.mission && (
                    <p className="text-[12.5px] text-[var(--org-slate)] mt-0.5">Mission {l.mission}</p>
                  )}
                </div>

                <p className="org-num text-[15px] font-medium text-[var(--org-ink)] text-right shrink-0 mt-2 sm:mt-0 sm:w-24">
                  {euros(l.total)}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 sm:ml-auto sm:max-w-xs">
            {[
              { l: "Séjours", v: data.totals.reservations },
              { l: "Nuits",   v: data.totals.nights },
              { l: "Dont taxe de séjour", v: euros(data.totals.taxes) },
            ].map(x => (
              <div key={x.l} className="flex justify-between py-1.5 text-[13.5px] text-[var(--org-slate)]">
                <span>{x.l}</span>
                <span className="org-num text-[var(--org-ink)]">{x.v}</span>
              </div>
            ))}
            <div
              className="flex justify-between items-baseline pt-3 mt-2"
              style={{ borderTop: "1px solid var(--org-line)" }}
            >
              <span className="text-[14px] text-[var(--org-ink)]">Total à régler</span>
              <span className="org-num text-[22px] font-medium text-[var(--org-ink)]">
                {euros(data.totals.total)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
